// src/pages/ReceiptPDFTemplate.jsx
import { FileText, Loader2 } from "lucide-react";

const ReceiptPDFTemplate = ({ receipt, loading }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const formatCurrency = (amount, currency = 'NGN') => {
        if (amount == null) return '—';
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: currency || 'NGN',
            minimumFractionDigits: 2
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 size={40} className="animate-spin text-gmail-blue" />
            </div>
        );
    }

    if (!receipt) {
        return (
            <div className="text-center py-20 text-gray-500">
                <FileText size={64} className="mx-auto mb-6 text-gray-300" />
                <p>Receipt details not available.</p>
            </div>
        );
    }

    const subtotal = receipt.totalproduct || 0;
    const discount = receipt.totaldiscount || 0;
    const tax = receipt.totaltax || 0;
    const total = subtotal - discount + tax;

    return (
        <div className="bg-white shadow-xl rounded-lg overflow-hidden font-sans antialiased print:shadow-none">
            <div className="w-full" style={{ aspectRatio: '1 / 1.414', maxWidth: '210mm', margin: '0 auto' }}>
                <div className="p-12 h-full flex flex-col text-gray-800">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-12">
                        <div>
                            <h1 className="text-5xl font-bold text-gmail-blue">RECEIPT</h1>
                            <p className="text-3xl font-bold mt-3">#{receipt.idempotency_key}</p>
                        </div>
                        <div className="text-right">
                            <h2 className="text-2xl font-bold">{receipt.organization?.organization_name || 'Your Business'}</h2>
                            <p className="text-sm mt-1">{receipt.store?.identifier}</p>
                        </div>
                    </div>

                    {/* Bill To & Date */}
                    <div className="grid grid-cols-2 gap-12 mb-12">
                        <div>
                            <p className="font-bold text-lg mb-3">Sold To:</p>
                            <p className="font-semibold text-lg">
                                {receipt.customer?.user?.first_name} {receipt.customer?.user?.last_name}
                            </p>
                            {receipt.customer?.user?.first_name && <p className="text-gray-600">{receipt.customer.user.first_name} Corporation</p>}
                        </div>
                        <div className="text-right space-y-2">
                            <p><span className="font-bold">Date:</span> {formatDate(receipt.placed_on)}</p>
                            <p><span className="font-bold">Status:</span> <span className="text-green-600 font-bold">PAID</span></p>
                        </div>
                    </div>

                    {/* Line Items */}
                    <table className="w-full mb-12 text-sm">
                        <thead>
                            <tr className="border-b-2 border-gray-800 text-left">
                                <th className="pb-4 font-bold">Item</th>
                                <th className="pb-4 font-bold text-center">Qty</th>
                                <th className="pb-4 font-bold text-right">Price</th>
                                <th className="pb-4 font-bold text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {receipt.orderitems?.length > 0 ? (
                                receipt.orderitems.map((item, i) => (
                                    <tr key={i} className="border-b border-gray-200">
                                        <td className="py-4">{item.catentry.productname}</td>
                                        <td className="py-4 text-center">{item.quantity}</td>
                                        <td className="py-4 text-right">{formatCurrency(item.price, item.currency)}</td>
                                        <td className="py-4 text-right font-medium">
                                            {formatCurrency(item.price * item.quantity, item.currency)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={4} className="py-8 text-center text-gray-500">No items</td></tr>
                            )}
                        </tbody>
                    </table>

                    {/* Totals */}
                    <div className="ml-auto w-80 mt-auto">
                        <div className="space-y-3 text-lg">
                            <div className="flex justify-between">
                                <span>Subtotal:</span>
                                <span>{formatCurrency(subtotal, receipt.currency)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-green-600">
                                    <span>Discount:</span>
                                    <span>-{formatCurrency(discount, receipt.currency)}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span>Tax:</span>
                                <span>{formatCurrency(tax, receipt.currency)}</span>
                            </div>
                            <div className="flex justify-between pt-4 border-t-2 border-gray-800 text-2xl font-bold">
                                <span>Total Paid:</span>
                                <span className="text-gmail-blue">{formatCurrency(total, receipt.currency)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 pt-8 border-t border-gray-300 text-center text-sm text-gray-600">
                        <p className="font-medium text-lg">Thank you for your purchase!</p>
                        <p className="mt-2">This is your official receipt. Goods sold are non-returnable unless defective.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReceiptPDFTemplate;